import { useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fromJS } from "immutable";
import { AppContext } from "./Contexts";
import SearchBox from "./main/component/SearchBox";
import { getJirItemUrl } from "./api/api";
import { navigateTo } from "./Navigator";
import { Eye, Mail, PanelLeftOpen, SquarePen, Star, StarOff } from "lucide-react";
import LayoutAction from "./actions/LayoutAction";
import { getCurrentItemAppName, listNav } from "./AppNav";
import { useQuery } from "./AppLayout";
import { saveFavoriteTree } from "./api/ProjectApi";
import ProjectAction from "./actions/ProjectAction";
import { useTranslation } from "react-i18next";
import { FavoriteTreeNode, FavoriteTreeNodeTypeNotFolder } from "./AppNavFavoriteTree";
import CopyLinkButton from "./main/CopyLinkButton";

const SearchContainer = ({ onSelectItem, className = "" }) => (
  <div className={`flex items-center ${className}`}>
    <SearchBox onSelectItem={onSelectItem} collapsible={true} />
  </div>
);

const ActionsContainer = ({ children, className = "" }) => (
  <div className={`flex items-center flex-shrink-0 ml-2 ${className}`}>{children}</div>
);

export const handleOnClickProject = (project, appContext, navigate) => {
  const link = `${appContext.routePrefix}/${project.id}?view=BOARD&type=SALE&key=Deals`;
  const jiraLink = getJirItemUrl({
    workspaceId: project.id,
    key: "Deals",
    type: "SALE",
  });
  navigateTo({
    jiraUrlParams: { relativeUrl: jiraLink },
    appUrl: link,
    navigate,
  });
};

export const handleClickItem = (item, navigate) => {
  const jiraLink = getJirItemUrl({
    workspaceId: item.get("projectId"),
    itemId: item.get("id"),
    type: item.get("type"),
    key: "Deals",
  });
  const appUrl = `/workspaces/${item.get("projectId")}/items/${item.get("id")}?type=${item.get(
    "type"
  )}&key=Deals`;
  navigateTo({ jiraUrlParams: { relativeUrl: jiraLink }, appUrl, navigate });
};

const getAllFavorite = (tree = [], nodeType: FavoriteTreeNodeTypeNotFolder) => {
  let result = [];
  for (const node of tree) {
    if (node.nodeType === nodeType) {
      result.push(node);
    }
    if (node.children?.length) {
      result = result.concat(getAllFavorite(node.children, nodeType));
    }
  }
  return result;
};

const removeFavoriteById = (
  tree = [],
  nodeType: FavoriteTreeNodeTypeNotFolder,
  idToRemove: string
) => {
  let fieldId = "id";
  if (nodeType === "SavedFilter") {
    fieldId = "filterId";
  } else if (nodeType === "ItemLink") {
    fieldId = "itemId";
  } else if (nodeType === "Dashboard") {
    fieldId = "dashboardId";
  } else if (nodeType === "Automation") {
    fieldId = "automationId";
  }
  return tree
    .filter((node) => !(node.nodeType === nodeType && node[fieldId] === idToRemove))
    .map((node) => ({
      ...node,
      children: node.children?.length
        ? removeFavoriteById(node.children, nodeType, idToRemove)
        : [],
    }));
};

const AppBreadcrumb = ({ suffixItems = null }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, itemId, dashboardId, ruleId }: any = useParams();
  const query: any = useQuery();
  const contentType = query.get("type");

  const appContext = useContext<any>(AppContext);

  const showAppNav = useSelector((state: any) => state.layoutReducer.get("appNav"));
  const favoriteTree =
    useSelector((state: any) => state.projectReducer.getIn(["project", "favoriteTree"])) ||
    fromJS([]);
  const task =
    useSelector((state: any) => state.taskReducer.getIn(["task", contentType])) || fromJS({});
  const dashboards = useSelector((state: any) => state.dashboard.get("dashboards")) || fromJS([]);
  const dashboard = dashboards.find((item) => item.get("id") === dashboardId) || fromJS({});
  const rule = useSelector((state: any) => state.ruleReducer.get("rule"));

  const renderAppTitle = useMemo(() => {
    const handleClickAddFavorite = async (node: FavoriteTreeNode) => {
      const updatedFavoriteTree = [...favoriteTree.toJS(), node];
      await saveFavoriteTree(projectId, updatedFavoriteTree);
      dispatch(ProjectAction.getFavoriteTree(projectId));
      dispatch(
        LayoutAction.setNotification({
          title: t("Favorites"),
          type: "success",
          message: t("Add to favorite successfully"),
        })
      );
    };

    const handleClickRemoveFavorite = async (
      nodeType: FavoriteTreeNodeTypeNotFolder,
      idToRemove: string
    ) => {
      const updatedFavoriteTree = removeFavoriteById(favoriteTree.toJS(), nodeType, idToRemove);
      await saveFavoriteTree(projectId, updatedFavoriteTree);
      dispatch(ProjectAction.getFavoriteTree(projectId));
      dispatch(
        LayoutAction.setNotification({
          title: t("Favorites"),
          type: "success",
          message: t("Remove from favorites successfully"),
        })
      );
    };

    const renderFavoriteForItem = () => {
      if (!itemId) return null;
      if (task.get("id") !== itemId) return null;
      const favoriteFilters = getAllFavorite(favoriteTree.toJS(), "ItemLink").map(
        (favorite) => favorite.itemId
      );
      const isFavorite = favoriteFilters.includes(itemId);
      const itemName = task.getIn(["data", "name", "value"]) || itemId;
      return (
        <div
          className="group ml-2 p-1 rounded-md hover:bg-gray-100 cursor-pointer"
          title={isFavorite ? t("Remove from favorites") : t("Add to favorite")}
          onClick={() => {
            if (isFavorite) {
              handleClickRemoveFavorite("ItemLink", itemId);
            } else {
              handleClickAddFavorite({
                id: `ItemLink-${Date.now()}`,
                name: itemName,
                nodeType: "ItemLink",
                itemType: contentType,
                itemId: itemId,
                iconUrl: task.get("iconUrl") || "",
                children: [],
              });
            }
          }}
        >
          {isFavorite ? (
            <StarOff className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
          ) : (
            <Star className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
          )}
        </div>
      );
    };

    const renderFavoriteForDashboard = () => {
      if (!dashboardId) return null;
      if (dashboard.get("id") !== dashboardId) return null;
      const favoriteFilters = getAllFavorite(favoriteTree.toJS(), "Dashboard").map(
        (favorite) => favorite.dashboardId
      );
      const isFavorite = favoriteFilters.includes(dashboardId);
      const dashboardName = dashboard.get("name") || dashboardId;
      return (
        <div
          className="group ml-2 p-1 rounded-md hover:bg-gray-100 cursor-pointer"
          title={isFavorite ? t("Remove from favorites") : t("Add to favorite")}
          onClick={() => {
            if (isFavorite) {
              handleClickRemoveFavorite("Dashboard", dashboardId);
            } else {
              handleClickAddFavorite({
                id: `Dashboard-${Date.now()}`,
                name: dashboardName,
                nodeType: "Dashboard",
                dashboardId: dashboardId,
                children: [],
              });
            }
          }}
        >
          {isFavorite ? (
            <StarOff className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
          ) : (
            <Star className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
          )}
        </div>
      );
    };

    const renderFavoriteForAutomation = () => {
      if (!ruleId) return null;
      if (rule.get("id") !== ruleId) return null;
      const favoriteFilters = getAllFavorite(favoriteTree.toJS(), "Automation").map(
        (favorite) => favorite.automationId
      );
      const isFavorite = favoriteFilters.includes(ruleId);
      const automationName = [
        rule.getIn(["startEvent", "data", "description"]),
        rule.getIn(["startEvent", "data", "to", "label"]) ? "to" : null,
        rule.getIn(["startEvent", "data", "to", "label"]),
      ]
        .filter(Boolean)
        .join(" ");
      return (
        <div
          className="group ml-2 p-1 rounded-md hover:bg-gray-100 cursor-pointer"
          title={isFavorite ? t("Remove from favorites") : t("Add to favorite")}
          onClick={() => {
            if (isFavorite) {
              handleClickRemoveFavorite("Automation", ruleId);
            } else {
              handleClickAddFavorite({
                id: `Automation-${Date.now()}`,
                name: automationName,
                nodeType: "Automation",
                automationId: ruleId,
                children: [],
              });
            }
          }}
        >
          {isFavorite ? (
            <StarOff className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
          ) : (
            <Star className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
          )}
        </div>
      );
    };

    let icon = null;
    let currentItemAppName = getCurrentItemAppName(query, location.pathname);
    const navItem = listNav.find((item) => item.name === currentItemAppName);
    if (navItem) {
      icon = <div className="h-5 w-5 flex items-center justify-center"><navItem.icon size={20} /></div>;
      currentItemAppName = navItem.name;
    }
    // if detail item: SALE, COMPANY, CONTACT, PRODUCT
    if (itemId && itemId === task.get("id")) {
      if (task.get("type") !== "SALE" && task.get("type") !== "PRODUCT") {
        icon = (
          <div>
            <img
              src={task.get("iconUrl")}
              alt={task.getIn(["data", "name", "value"])}
              className="w-5 h-5 rounded-full object-cover"
            />
          </div>
        );
      }
      return (
        <div className="group flex items-center gap-2">
          {icon}
          <span>{task.getIn(["data", "name", "value"]) || currentItemAppName}</span>
          {renderFavoriteForItem()}
          <CopyLinkButton link={window.location.href} />
        </div>
      );
    }
    // if dashboard
    if (dashboardId && dashboardId === dashboard.get("id")) {
      return (
        <div className="group flex items-center gap-2">
          {icon}
          <span
            className="cursor-pointer"
            onClick={() => {
              const appUrl = `/workspaces/${projectId}/reports?key=Reports`;
              navigateTo({
                jiraUrlParams: {
                  relativeUrl: getJirItemUrl({
                    workspaceId: projectId,
                    view: "CHART",
                    key: "Reports",
                    suffix: "reports",
                  }),
                },
                appUrl,
                navigate,
              });
            }}
          >
            {currentItemAppName}
          </span>
          <div className="h-4 border-r border-gray-200"></div>
          <span>{dashboard.get("name")}</span>
          {renderFavoriteForDashboard()}
          <CopyLinkButton link={window.location.href} />
        </div>
      );
    }
    // if rule
    if (ruleId && ruleId === rule.get("id")) {
      const automationName = [
        rule.getIn(["startEvent", "data", "description"]),
        rule.getIn(["startEvent", "data", "to", "label"]) ? "to" : null,
        rule.getIn(["startEvent", "data", "to", "label"]),
      ]
        .filter(Boolean)
        .join(" ");
      return (
        <div className="group flex items-center gap-2">
          {icon}
          <span
            className="cursor-pointer"
            onClick={() => {
              const appUrl = `/workspaces/${projectId}/automations?key=Automations`;
              navigateTo({
                jiraUrlParams: {
                  relativeUrl: getJirItemUrl({
                    workspaceId: projectId,
                    view: "GRID",
                    type: "SALE",
                    suffix: "automations",
                    key: "Automations",
                  }),
                },
                appUrl,
                navigate,
              });
            }}
          >
            {currentItemAppName}
          </span>
          <div className="h-4 border-r border-gray-200"></div>
          <span>{automationName}</span>
          {renderFavoriteForAutomation()}
          <CopyLinkButton link={window.location.href} />
        </div>
      );
    }
    if (
      currentItemAppName === "Drafts" ||
      currentItemAppName === "Outbox" ||
      currentItemAppName === "Tracked"
    ) {
      return (
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer ${
              currentItemAppName === "Drafts"
                ? "border border-gray-200 bg-gray-100"
                : "hover:bg-gray-100"
            }`}
            onClick={() => {
              const appUrl = `/workspaces/${projectId}/?view=GRID&type=ACTIVITY&key=Drafts`;
              navigateTo({
                jiraUrlParams: {
                  relativeUrl: getJirItemUrl({
                    workspaceId: projectId,
                    view: "GRID",
                    type: "ACTIVITY",
                    key: "Drafts",
                  }),
                },
                appUrl,
                navigate,
              });
            }}
          >
            <SquarePen
              className={`h-4 w-4 ${currentItemAppName === "Drafts" ? "" : "text-gray-600"}`}
            />
            <span className={currentItemAppName === "Drafts" ? "" : "text-gray-600"}>
              {t("Drafts")}
            </span>
          </div>
          <div
            className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer ${
              currentItemAppName === "Outbox"
                ? "border border-gray-200 bg-gray-100"
                : "hover:bg-gray-100"
            }`}
            onClick={() => {
              const appUrl = `/workspaces/${projectId}/?view=GRID&type=ACTIVITY&key=Outbox`;
              navigateTo({
                jiraUrlParams: {
                  relativeUrl: getJirItemUrl({
                    workspaceId: projectId,
                    view: "GRID",
                    type: "ACTIVITY",
                    key: "Outbox",
                  }),
                },
                appUrl,
                navigate,
              });
            }}
          >
            <Mail className={`h-4 w-4 ${currentItemAppName === "Outbox" ? "" : "text-gray-600"}`} />
            <span className={currentItemAppName === "Outbox" ? "" : "text-gray-600"}>
              {t("Outbox")}
            </span>
          </div>
          <div
            className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer ${
              currentItemAppName === "Tracked"
                ? "border border-gray-200 bg-gray-100"
                : "hover:bg-gray-100"
            }`}
            onClick={() => {
              const appUrl = `/workspaces/${projectId}/?view=GRID&type=ACTIVITY&key=Tracked`;
              navigateTo({
                jiraUrlParams: {
                  relativeUrl: getJirItemUrl({
                    workspaceId: projectId,
                    view: "GRID",
                    type: "ACTIVITY",
                    key: "Tracked",
                  }),
                },
                appUrl,
                navigate,
              });
            }}
          >
            <Eye className={`h-4 w-4 ${currentItemAppName === "Tracked" ? "" : "text-gray-600"}`} />
            <span className={currentItemAppName === "Tracked" ? "" : "text-gray-600"}>
              {t("Tracked")}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="group flex items-center gap-2">
        {icon}
        <span>{currentItemAppName}</span>
        <CopyLinkButton link={window.location.href} />
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    location.pathname,
    contentType,
    projectId,
    itemId,
    dashboardId,
    ruleId,
    task,
    dashboard,
    rule,
    favoriteTree,
  ]);

  if (appContext.appName !== "main" && appContext.appName !== "chrome") {
    return null;
  }

  const handleSelectItem = (item) => {
    handleClickItem(item, navigate);
  };

  return (
    <div className="h-24 md:h-12 border-b border-gray-200">
      <nav className="h-12 md:h-full w-full flex items-center justify-between px-2 py-1 bg-white">
        <div className="flex items-center">
          {!showAppNav ? (
            <div className="pr-4 cursor-pointer">
              <PanelLeftOpen
                className="w-5 h-5 text-gray-500"
                onClick={() => {
                  dispatch(LayoutAction.showAppNav(true));
                }}
              />
            </div>
          ) : null}
          {renderAppTitle}
        </div>

        <div className="flex items-center">
          <div className="hidden md:flex items-center">
            <SearchContainer onSelectItem={handleSelectItem} />
          </div>
          <ActionsContainer>{suffixItems}</ActionsContainer>
        </div>
      </nav>

      {/* Mobile bottom row with search box */}
      <div className="w-full px-2 pb-2 bg-white md:hidden">
        <SearchContainer onSelectItem={handleSelectItem} />
      </div>
    </div>
  );
};

export default AppBreadcrumb;
